import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import { useApplicationContext } from '~/shared/context/use-application-context';

import {
    type GrowthCompanyGoal,
    type GrowthGoalDetail,
    type GrowthMyGoals,
    type GrowthOrganization,
    type GrowthRecommendations,
    type GrowthTeam,
    growthCompanyGoalsSchema,
    growthGoalDetailSchema,
    growthMyGoalsSchema,
    growthOrganizationSchema,
    growthRecommendationsSchema,
    growthTeamSchema,
} from '../../data-contract/growth';

export const growthPathnames = {
    companyGoal: (companyGoalId: string): `/${string}` => `/growth/company-goals/${companyGoalId}`,
    companyGoals: '/growth/company-goals',
    goal: (goalId: string): `/${string}` => `/growth/goals/${goalId}`,
    goalActions: (goalId: string): `/${string}` => `/growth/goals/${goalId}/actions`,
    goals: '/growth/goals',
    me: '/growth/me',
    organization: '/growth/organization',
    regenerateRecommendations: '/growth/recommendations/regenerate',
    recommendations: '/growth/recommendations',
    team: '/growth/team',
} as const;

const RECOMMENDATIONS_POLL_MS = 2000;
const ORGANIZATION_PAGE_SIZE = 50;

// The UI language is the last key part: the backend localises titles and generates suggestions in the Accept-Language
// language, so switching the UI language must refetch instead of serving the cache of the old language.
export const growthKeys = {
    all: ['growth'] as const,
    goal: (goalId: string, language: string) => ['growth', 'goal', goalId, language] as const,
    companyGoals: (language: string) => ['growth', 'company-goals', language] as const,
    me: (language: string) => ['growth', 'me', language] as const,
    organization: (language: string) => ['growth', 'organization', language] as const,
    recommendations: (language: string) => ['growth', 'recommendations', language] as const,
    team: (language: string) => ['growth', 'team', language] as const,
};

export const useMyGoals = (): UseQueryResult<GrowthMyGoals> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useApplicationContext();

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(growthMyGoalsSchema, {
                pathname: growthPathnames.me,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: growthKeys.me(language.code),
    });
};

export const useTeamGoals = (enabled: boolean): UseQueryResult<GrowthTeam> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useApplicationContext();

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(growthTeamSchema, {
                pathname: growthPathnames.team,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: growthKeys.team(language.code),
    });
};

export const useGoalDetail = (goalId: string): UseQueryResult<GrowthGoalDetail> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useApplicationContext();

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(growthGoalDetailSchema, {
                pathname: growthPathnames.goal(goalId),
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: growthKeys.goal(goalId, language.code),
    });
};

export const useCompanyGoals = (): UseQueryResult<readonly GrowthCompanyGoal[]> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useApplicationContext();

    return useQuery({
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(growthCompanyGoalsSchema, {
                pathname: growthPathnames.companyGoals,
            });

            if (result.isOk) {
                return result.value.companyGoals;
            }

            throw result.error;
        },
        queryKey: growthKeys.companyGoals(language.code),
    });
};

export const useOrganizationGoals = (enabled: boolean): UseQueryResult<GrowthOrganization> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useApplicationContext();

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(growthOrganizationSchema, {
                pathname: growthPathnames.organization,
                requestOptions: { searchParams: { page: 0, size: ORGANIZATION_PAGE_SIZE } },
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: growthKeys.organization(language.code),
    });
};

export const useGoalRecommendations = (enabled: boolean): UseQueryResult<GrowthRecommendations> => {
    const {
        applicationState: { language },
        validatingCobrainerApiFetcher,
    } = useApplicationContext();

    return useQuery({
        enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(growthRecommendationsSchema, {
                pathname: growthPathnames.recommendations,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey: growthKeys.recommendations(language.code),
        refetchInterval: (query) => (query.state.data?.status === 'pending' ? RECOMMENDATIONS_POLL_MS : false),
        staleTime: Number.POSITIVE_INFINITY,
    });
};
